import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight } from 'lucide-react';

const WHATSAPP_NUMBER = '5521998384393';
const WHATSAPP_BASE = `https://wa.me/${WHATSAPP_NUMBER}?text=`;

const courses = [
  {
    id: 1,
    title: 'Limpeza de Pele',
    image: 'https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/e5g6ur2n_curso%201.jpeg',
    description: 'Técnicas profissionais de limpeza facial completa',
    includes: ['Certificação', 'Apostila', 'Material', 'Suporte pós-curso'],
  },
  {
    id: 2,
    title: 'Micropigmentação Sobrancelha',
    subtitle: '(Dermografo)',
    image: 'https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/i1ms4rix_curso%202.jpeg',
    description: 'Domine a arte da micropigmentação com dermógrafo',
    includes: ['Certificação', 'Apostila', 'Material', 'Suporte pós-curso'],
  },
  {
    id: 3,
    title: 'Jato de Plasma',
    image: 'https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/jizzs87z_curso%203.jpeg',
    description: 'Tecnologia avançada para tratamentos estéticos',
    includes: ['Certificação', 'Apostila', 'Material', 'Suporte pós-curso'],
  },
  {
    id: 4,
    title: 'Mini Tattoo',
    image: 'https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/adt5nlj8_curso%204.jpeg',
    description: 'Aprenda a arte de mini tatuagens delicadas',
    includes: ['Certificação', 'Apostila', 'Material', 'Suporte pós-curso'],
  },
  {
    id: 5,
    title: 'Micropigmentação Labial',
    image: 'https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/vbihqo5o_curso%205.jpeg',
    description: 'Técnica de pigmentação labial com efeito natural',
    includes: ['Certificação', 'Apostila', 'Material', 'Suporte pós-curso'],
  },
  {
    id: 6,
    title: 'Microblading Sobrancelha',
    subtitle: '(Módulo Iniciante)',
    image: 'https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/njwrmku5_curso%206.jpeg',
    description: 'Curso para iniciantes em microblading',
    includes: ['Certificação', 'Apostila', 'Material', 'Suporte pós-curso'],
  },
];

const CourseCard = ({ course, index, isInView }) => {
  const whatsappMessage = encodeURIComponent(
    `Olá! Vim pelo site e tenho interesse no curso de ${course.title}. Gostaria de mais informações.`
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative glass-card rounded-2xl overflow-hidden"
      data-testid={`course-card-${course.id}`}
    >
      {/* Course Image */}
      <div className="relative h-48 sm:h-56 overflow-hidden">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading text-lg sm:text-xl font-bold text-white mb-1 group-hover:text-gold transition-colors">
          {course.title}
        </h3>
        {course.subtitle && (
          <p className="text-gold text-sm mb-2">{course.subtitle}</p>
        )}
        <p className="text-white/60 text-sm mb-4">
          {course.description}
        </p>

        {/* CTA Button with Gold Glow */}
        <motion.a
          href={`${WHATSAPP_BASE}${whatsappMessage}`}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-bold px-6 py-3 rounded-full transition-all duration-300 hover:bg-gold-light group/btn"
          style={{
            boxShadow: '0 0 20px rgba(201, 160, 80, 0.4), 0 0 40px rgba(201, 160, 80, 0.2)',
          }}
          whileHover={{ 
            scale: 1.02,
            boxShadow: '0 0 30px rgba(201, 160, 80, 0.6), 0 0 60px rgba(201, 160, 80, 0.4)',
          }}
          whileTap={{ scale: 0.98 }}
          data-testid={`course-cta-${course.id}`}
        >
          Quero me Inscrever
          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
        </motion.a>
      </div>
    </motion.div>
  );
};

export const Courses = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="cursos"
      ref={ref}
      data-testid="courses-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-gold font-medium tracking-wider uppercase text-sm mb-4">
            Nossos Cursos Presenciais
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Formações que <span className="text-gold">Transformam</span> Carreiras
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Escolha sua especialização e comece sua jornada rumo ao sucesso no mercado da estética. Rio de Janeiro.
          </p>
        </motion.div>

        {/* Courses Grid - 6 courses */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, index) => (
            <CourseCard
              key={course.id}
              course={course}
              index={index}
              isInView={isInView}
            />
          ))}
        </div>

        {/* Contact info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="text-white/50 text-sm mb-4">
            Todos os cursos incluem: Certificação • Apostila • Material • Suporte pós-curso
          </p>
          <p className="text-gold font-medium">
            Instrutora: Ritha Barreto | Rio de Janeiro
          </p>
        </motion.div>
      </div>
    </section>
  );
};
